import type { IProductDomain, IUseProductDomainsResult } from '@/Interfaces/ProductDomains/ProductDomain.interface';
import { PRODUCT_DOMAINS_ENDPOINT } from '@/constants/endpoints';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useEffect, useState } from 'react';

const useProductDomains = (productId: number | null | undefined): IUseProductDomainsResult => {
  const [domains, setDomains] = useState<IProductDomain[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!productId) {
      setDomains([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    // Drop the previous product's domains, or they render as this product's while loading.
    setDomains([]);
    setIsLoading(true);
    setError(null);

    axios
      .get<IProductDomain[]>(PRODUCT_DOMAINS_ENDPOINT(productId))
      .then(({ data }) => {
        if (cancelled) return;
        setDomains(data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        Sentry.captureException(err);
        setError(err);
        setDomains([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  return { domains, isLoading, error };
};

export default useProductDomains;
